import { useState } from 'react'
import { useFleets, useCreateFleet, useDeleteFleet, useUpdateFleet, usePlaybooks, useDevices } from '@/api/devices'
import { RequirePermission } from '@/components/RequirePermission'
import type { Fleet } from '@/types'

interface Props {
  tenantId: string | null
}

const inputStyle: React.CSSProperties = {
  background: 'var(--color-surface-2)',
  border: '1px solid var(--color-border)',
  color: 'var(--color-text)',
  padding: '8px 12px',
  borderRadius: 'var(--radius-sm)',
  fontSize: 13,
  width: '100%',
}

// ── Edit modal ───────────────────────────────────────────────────────────────

interface EditFleetModalProps {
  fleet: Fleet
  tenantId: string
  playbooks: Array<{ name: string }>
  onClose: () => void
}

function EditFleetModal({ fleet, tenantId, playbooks, onClose }: EditFleetModalProps) {
  const updateFleet = useUpdateFleet(tenantId)
  const [name, setName] = useState(fleet.name)
  const [description, setDescription] = useState(fleet.description ?? '')
  const [playbook, setPlaybook] = useState(fleet.bootstrap_playbook ?? '')

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    await updateFleet.mutateAsync({
      fleetId: fleet.id,
      req: {
        name: name.trim() || undefined,
        description: description.trim() || undefined,
        bootstrap_playbook: playbook || undefined,
      },
    })
    onClose()
  }

  return (
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={e => { if (e.target === e.currentTarget) onClose() }}
    >
      <div style={{ background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius)', padding: 24, width: 460, maxWidth: '95vw', boxShadow: '0 8px 32px rgba(0,0,0,0.4)' }}>
        <h3 style={{ margin: '0 0 18px', fontSize: 15, fontWeight: 600 }}>Edit fleet — {fleet.name}</h3>
        <form onSubmit={handleSubmit}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div>
              <label style={{ fontSize: 12, color: 'var(--color-text-muted)', display: 'block', marginBottom: 4 }}>Name</label>
              <input style={inputStyle} value={name} onChange={e => setName(e.target.value)} required />
            </div>
            <div>
              <label style={{ fontSize: 12, color: 'var(--color-text-muted)', display: 'block', marginBottom: 4 }}>Description</label>
              <input style={inputStyle} value={description} onChange={e => setDescription(e.target.value)} placeholder="Optional" />
            </div>
            <div>
              <label style={{ fontSize: 12, color: 'var(--color-text-muted)', display: 'block', marginBottom: 4 }}>Bootstrap playbook</label>
              <select style={{ ...inputStyle, cursor: 'pointer' }} value={playbook} onChange={e => setPlaybook(e.target.value)}>
                <option value="">— Platform default —</option>
                {playbooks.map(p => <option key={p.name} value={p.name}>{p.name}</option>)}
              </select>
            </div>
          </div>
          {updateFleet.isError && (
            <div style={{ color: 'var(--color-error)', fontSize: 12, marginTop: 10 }}>Save failed. Check the console for details.</div>
          )}
          <div style={{ display: 'flex', gap: 8, marginTop: 18, justifyContent: 'flex-end' }}>
            <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={updateFleet.isPending}>
              {updateFleet.isPending ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export function Fleets({ tenantId }: Props) {
  const [showCreate, setShowCreate] = useState(false)
  const [editFleet, setEditFleet] = useState<Fleet | null>(null)
  const [newName, setNewName] = useState('')
  const [newDesc, setNewDesc] = useState('')
  const [newPlaybook, setNewPlaybook] = useState('')

  const { data, isLoading, error } = useFleets(tenantId)
  const { data: playbooksData } = usePlaybooks(tenantId)
  const { data: devicesData } = useDevices(tenantId)
  const createFleet = useCreateFleet(tenantId ?? '')
  const deleteFleet = useDeleteFleet(tenantId ?? '')

  const fleets = data?.data ?? []

  // Count devices per fleet for the summary column
  const deviceCountByFleet = (devicesData?.data ?? []).reduce<Record<string, number>>((acc, d) => {
    if (d.fleet_id) acc[d.fleet_id] = (acc[d.fleet_id] ?? 0) + 1
    return acc
  }, {})

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault()
    if (!tenantId || !newName.trim()) return
    await createFleet.mutateAsync({
      name: newName.trim(),
      description: newDesc.trim() || undefined,
      bootstrap_playbook: newPlaybook || undefined,
    })
    setNewName('')
    setNewDesc('')
    setNewPlaybook('')
    setShowCreate(false)
  }

  if (!tenantId) {
    return <div className="empty-state">Select a tenant to manage fleets.</div>
  }

  return (
    <div>
      {editFleet && (
        <EditFleetModal
          fleet={editFleet}
          tenantId={tenantId}
          playbooks={playbooksData ?? []}
          onClose={() => setEditFleet(null)}
        />
      )}
      <div className="page-header">
        <div>
          <h1 className="page-title">Fleets</h1>
          <p className="page-subtitle">{data?.total ?? 0} total</p>
        </div>
        <RequirePermission perm="fleets:write">
          <button className="btn btn-primary" onClick={() => setShowCreate(!showCreate)}>
            + New fleet
          </button>
        </RequirePermission>
      </div>

      {showCreate && (
        <form
          onSubmit={handleCreate}
          style={{
            background: 'var(--color-surface)',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius)',
            padding: 20,
            marginBottom: 20,
            display: 'flex',
            gap: 12,
            flexWrap: 'wrap',
            alignItems: 'flex-end',
          }}
        >
          <div>
            <label style={{ fontSize: 12, color: 'var(--color-text-muted)', display: 'block', marginBottom: 4 }}>
              Fleet name *
            </label>
            <input
              value={newName}
              onChange={e => setNewName(e.target.value)}
              placeholder="e.g. factory-line-a"
              required
              style={{
                background: 'var(--color-surface-2)',
                border: '1px solid var(--color-border)',
                color: 'var(--color-text)',
                padding: '8px 12px',
                borderRadius: 'var(--radius-sm)',
                fontSize: 13,
                width: 220,
              }}
            />
          </div>
          <div>
            <label style={{ fontSize: 12, color: 'var(--color-text-muted)', display: 'block', marginBottom: 4 }}>
              Description
            </label>
            <input
              value={newDesc}
              onChange={e => setNewDesc(e.target.value)}
              placeholder="Optional description"
              style={{
                background: 'var(--color-surface-2)',
                border: '1px solid var(--color-border)',
                color: 'var(--color-text)',
                padding: '8px 12px',
                borderRadius: 'var(--radius-sm)',
                fontSize: 13,
                width: 280,
              }}
            />
          </div>
          <div>
            <label style={{ fontSize: 12, color: 'var(--color-text-muted)', display: 'block', marginBottom: 4 }}>
              Bootstrap playbook
            </label>
            <select
              value={newPlaybook}
              onChange={e => setNewPlaybook(e.target.value)}
              style={{
                background: 'var(--color-surface-2)',
                border: '1px solid var(--color-border)',
                color: 'var(--color-text)',
                padding: '8px 12px',
                borderRadius: 'var(--radius-sm)',
                fontSize: 13,
                width: 220,
                cursor: 'pointer',
              }}
            >
              <option value="">— Platform default —</option>
              {(playbooksData ?? []).map(p => (
                <option key={p.name} value={p.name}>{p.name}</option>
              ))}
            </select>
          </div>
          <button type="submit" className="btn btn-primary" disabled={createFleet.isPending}>
            {createFleet.isPending ? 'Creating…' : 'Create'}
          </button>
          <button type="button" className="btn btn-ghost" onClick={() => setShowCreate(false)}>
            Cancel
          </button>
        </form>
      )}

      {isLoading && <div className="empty-state">Loading…</div>}
      {error && <div className="empty-state" style={{ color: 'var(--color-danger)' }}>Error loading fleets.</div>}

      {!isLoading && fleets.length === 0 && (
        <div className="empty-state">No fleets yet. Create one to group your devices.</div>
      )}

      {fleets.length > 0 && (
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Devices</th>
                <th>Description</th>
                <th>Bootstrap playbook</th>
                <th>Created</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {fleets.map(fleet => (
                <tr key={fleet.id}>
                  <td style={{ fontWeight: 500 }}>{fleet.name}</td>
                  <td style={{ color: 'var(--color-text-muted)', fontSize: 13 }}>
                    {deviceCountByFleet[fleet.id] ?? 0}
                  </td>
                  <td style={{ color: 'var(--color-text-muted)' }}>{fleet.description ?? '—'}</td>
                  <td style={{ color: 'var(--color-text-muted)', fontFamily: 'var(--font-mono)', fontSize: 12 }}>
                    {fleet.bootstrap_playbook ?? <span style={{ opacity: 0.45 }}>default</span>}
                  </td>
                  <td style={{ color: 'var(--color-text-muted)', fontSize: 12 }}>
                    {new Date(fleet.created_at).toLocaleDateString()}
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <RequirePermission perm="fleets:write">
                        <button
                          className="btn btn-ghost"
                          style={{ padding: '4px 10px', fontSize: 12 }}
                          onClick={() => setEditFleet(fleet)}
                        >
                          Edit
                        </button>
                      </RequirePermission>
                      <RequirePermission perm="fleets:delete">
                        <button
                          className="btn btn-danger"
                          style={{ padding: '4px 10px', fontSize: 12 }}
                          onClick={() => deleteFleet.mutate(fleet.id)}
                        >
                          Delete
                        </button>
                      </RequirePermission>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
